import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { GripPoint } from "./gripCalculation";
import { readNumber, resolveArrayAxisAngleDeg } from "./renderHelpers";

/**
 * World-space point passed to the array grip drag handler. Separated
 * from a bigger Point shape so this module has no upstream dependency
 * on the scene-internal StagePoint type.
 */
export type WorldPoint = { x: number; y: number };

/**
 * Property-patch payload delivered to the parent component's
 * onGripDrag callback. Matches the shape used throughout the scene
 * package for entity property edits.
 */
export type PropertyPatch = Record<string, string>;

/**
 * Apply a grip drag for a Rectangular Associative Array. Projects the
 * mouse position onto the array's constraint axis (column or row
 * rotated by axisAngle), then computes the appropriate property
 * update:
 *
 *   - column-count  (triangle on +X axis) -> patch Columns
 *   - row-count     (triangle on +Y axis) -> patch Rows
 *   - column-spacing (diamond on +X axis) -> patch Column spacing
 *   - row-spacing    (diamond on +Y axis) -> patch Row spacing
 *
 * Count grips are clamped to a minimum of 1 copy so the array never
 * collapses below a single instance. Spacing grips take the absolute
 * value and clamp to a minimum of 1 world unit so dragging past the
 * origin doesn't produce a negative / zero spacing.
 *
 * If the grip isn't one of the four roles handled here the function
 * is a no-op — callers are expected to have already dispatched origin
 * / free-drag grips through the default positional path.
 */
export function applyArrayGripDrag(
  grip: GripPoint,
  worldPoint: WorldPoint,
  entity: EntityPreview,
  onGripDrag: (patch: PropertyPatch) => void,
): void {
  const p = entity.properties;
  const hasBase = "Base X" in p || "Base Y" in p;
  const baseX = readNumber(p, hasBase ? "Base X" : "Origin X");
  const baseY = readNumber(p, hasBase ? "Base Y" : "Origin Y");
  // S-ARRAYRECT-AXISANGLE-UNIT (renderHelpers.ts:resolveArrayAxisAngleDeg) —
  // the grip is DISPLAYED at the converted-degrees position (gripCalculation.ts
  // uses the same resolver), so the drag must project the mouse delta onto
  // the SAME converted axis. Reading the raw property here (Codex P2, found
  // in review) would drag along the pre-fix ~1.6deg axis while the visible
  // grip sits on the real 90deg axis for an associative array.
  const axisDeg = resolveArrayAxisAngleDeg(p);
  const cosA = Math.cos((axisDeg * Math.PI) / 180);
  const sinA = Math.sin((axisDeg * Math.PI) / 180);

  // Convert mouse world delta -> local array frame. Column axis is
  // the +X direction in the unrotated frame, row axis is +Y.
  const dx = worldPoint.x - baseX;
  const dy = worldPoint.y - baseY;
  const localCol = dx * cosA + dy * sinA;
  const localRow = -dx * sinA + dy * cosA;
  const dist = grip.constraintAxis === "column" ? localCol : localRow;

  if (grip.gripRole === "column-count") {
    const colSpacing = readNumber(p, "Column spacing", 80);
    const newCount = Math.max(1, Math.round(dist / (colSpacing || 1)) + 1);
    onGripDrag({ Columns: String(newCount) });
    return;
  }
  if (grip.gripRole === "row-count") {
    const rowSpacing = readNumber(p, "Row spacing", 60);
    const newCount = Math.max(1, Math.round(dist / (rowSpacing || 1)) + 1);
    onGripDrag({ Rows: String(newCount) });
    return;
  }
  if (grip.gripRole === "column-spacing") {
    // Keep spacing strictly positive — dragging past the origin would
    // otherwise produce a zero / negative spacing that collapses the
    // preview.
    const newSpacing = Math.max(1, Math.abs(dist));
    onGripDrag({
      "Column spacing": String(Math.round(newSpacing * 100) / 100),
    });
    return;
  }
  if (grip.gripRole === "row-spacing") {
    const newSpacing = Math.max(1, Math.abs(dist));
    onGripDrag({
      "Row spacing": String(Math.round(newSpacing * 100) / 100),
    });
    return;
  }
}
